import * as React from "react";

import { cn } from "../../lib/utils";

/** Linear's 12–20px circular completion glyph — replaces every donut chart and ring stat card. */

export interface CircularProgressProps extends Omit<
  React.SVGAttributes<SVGSVGElement>,
  "color" | "width" | "height" | "role"
> {
  /** Completion percentage, 0–100. Values outside the range are clamped. */
  value: number;
  /** Rendered box in px. Default 14. */
  size?: 12 | 14 | 16 | 20;
  /** Stroke width in viewBox units (24 = the full box). Default 2. */
  strokeWidth?: number;
  /** Explicit indicator colour; defaults to the status token for the value. */
  color?: string;
  /** Draw the unfilled remainder. Default true. */
  showTrack?: boolean;
  /** Accessible name. Defaults to "<value>% complete". */
  label?: string;
}

const RADIUS = 10;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const CircularProgress = React.forwardRef<SVGSVGElement, CircularProgressProps>(
  (
    {
      value,
      size = 14,
      strokeWidth = 2,
      color,
      showTrack = true,
      label,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const clamped = Math.min(
      100,
      Math.max(0, Number.isFinite(value) ? value : 0),
    );
    const offset = CIRCUMFERENCE * (1 - clamped / 100);
    const complete = clamped >= 100;

    return (
      <svg
        ref={ref}
        data-slot="circular-progress"
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? `${Math.round(clamped)}% complete`}
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        className={cn(
          "shrink-0",
          color
            ? undefined
            : complete
              ? "text-status-done"
              : "text-status-progress",
          className,
        )}
        style={color ? { color, ...style } : style}
        {...props}
      >
        {showTrack ? (
          <circle
            cx="12"
            cy="12"
            r={RADIUS}
            strokeWidth={strokeWidth}
            className="stroke-line-strong"
          />
        ) : null}
        <circle
          cx="12"
          cy="12"
          r={RADIUS}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          transform="rotate(-90 12 12)"
          className="stroke-current transition-[stroke-dashoffset] duration-150 ease-out-fast"
        />
      </svg>
    );
  },
);
CircularProgress.displayName = "CircularProgress";

export { CircularProgress };
